import { getNiceTickValues } from "recharts-scale";
import { scaleUtc } from "d3-scale";
import { utcDay } from "d3-time";

export const getDomain = (range: [number, number], offsetInPercents: number, startFromZero: boolean): [min: number, max: number] => {
  const [min, max] = range;

  const minValue = startFromZero || min === max ? Math.min(min, 0) : min;
  const maxValue = startFromZero || min === max ? Math.max(max, 0) : max;

  const diff = Math.abs(maxValue - minValue);
  const offset = (diff * offsetInPercents) / 100;

  return [minValue !== 0 ? min - offset : 0, maxValue !== 0 ? max + offset : 0];
};

export const replaceExtraLargeValue = (value: number, domain?: [min: number, max: number]): number => {
  if (!domain) {
    return value;
  }

  const [min, max] = domain;
  const offset = Math.abs(max - min) * 3;

  if (value > max + offset) {
    return max + offset;
  }

  if (value < min - offset) {
    return min - offset;
  }

  return value;
};

export const getRoundedDomain = (domain: [number, number], decimalPlaces: number): { domain: [number, number]; multiplier: number } => {
  const [min, max] = domain;
  const value = max - min;
  const i = value === 0 ? value : Math.floor(Math.log(value) / Math.log(1000));

  const multiplier = i >= 0 ? Math.pow(1000, i) : Math.pow(10, -decimalPlaces);

  return {
    domain: [Math.round(min / multiplier), Math.round(max / multiplier)],
    multiplier,
  };
};

export const getTicks = (
  tickCount: number,
  limit: [min: number, max: number],
  { offsetInPercents = 15, decimalPlaces = 2, startFromZero = false }: { offsetInPercents?: number; decimalPlaces?: number; startFromZero?: boolean } = {}
): number[] | undefined => {
  if (tickCount < 2) {
    return undefined;
  }

  const domain = getDomain(limit, offsetInPercents, startFromZero);
  const rounded = getRoundedDomain(domain, decimalPlaces);
  const ticks = getNiceTickValues(rounded.domain, tickCount, false);

  return ticks.map(v => v * rounded.multiplier);
};

export const getXTicks = (limit: [min: number | Date, max: number | Date]) => {
  const [min, max] = limit;
  const startDate = new Date(min);
  const endDate = new Date(max);

  const domain = scaleUtc().domain([utcDay.floor(startDate), utcDay.ceil(endDate)]);
  const interval = utcDay.every(1);

  return {
    ticks: interval ? domain.ticks(interval) : domain.ticks(utcDay.count(startDate, endDate)),
    domain: domain.domain(),
  };
};
